/**
 * 의약품 데이터 생성 단계 초기화 모달 — POST /admin/drug-data/reset.
 * 단계를 고르고 기준월을 직접 입력해야 실행되며, 끝나면 대상별 삭제·백업 결과를 보여준다.
 * 복원 API 는 없고 지운 데이터는 같은 DB 백업 테이블에만 남는다.
 */
import {
  DRUG_DATA_RESET_TARGETS,
  type DrugDataResetResponse,
  type DrugDataResetTarget,
} from '@/entities/drug-data/api/drug-data.api';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface DrugDataResetModalProps {
  open: boolean;
  onClose: () => void;
  baseYm: string;
  /** 게시된 기준월은 서버가 409 로 거절하므로 미리 막고 게시 취소를 안내한다 */
  released: boolean;
  loading?: boolean;
  /** 전체 선택이면 undefined. 실패하면 null 을 돌려줘 선택 화면에 머문다 */
  onConfirm: (
    targets: DrugDataResetTarget[] | undefined,
  ) => Promise<DrugDataResetResponse | null>;
  onCancelRelease: () => void;
}

const ALL_TARGETS = DRUG_DATA_RESET_TARGETS.map((t) => t.value);

const targetLabel = (target: DrugDataResetTarget) =>
  DRUG_DATA_RESET_TARGETS.find((t) => t.value === target)?.label ?? target;

const formatYm = (ym: string) =>
  /^\d{6}$/.test(ym) ? `${ym.slice(0, 4)}년 ${ym.slice(4)}월` : ym;

export const DrugDataResetModal: React.FC<DrugDataResetModalProps> = ({
  open,
  onClose,
  baseYm,
  released,
  loading,
  onConfirm,
  onCancelRelease,
}) => {
  const [selected, setSelected] = useState<DrugDataResetTarget[]>([]);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState<DrugDataResetResponse | null>(null);

  // 열 때마다 비워, 이전 선택·확인 입력으로 실수 실행하지 않게 한다
  useEffect(() => {
    if (open) {
      setSelected([]);
      setConfirmText('');
      setResult(null);
    }
  }, [open]);

  const allSelected = selected.length === ALL_TARGETS.length;
  const confirmed = confirmText.trim() === baseYm;
  const canSubmit = selected.length > 0 && confirmed && !released && !loading;

  const toggle = (target: DrugDataResetTarget, checked: boolean) =>
    setSelected((prev) =>
      checked
        ? ALL_TARGETS.filter((t) => t === target || prev.includes(t))
        : prev.filter((t) => t !== target),
    );

  const handleConfirm = async () => {
    if (!canSubmit) return;
    const response = await onConfirm(allSelected ? undefined : selected);
    if (response) setResult(response);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !loading && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>의약품 데이터 초기화</DialogTitle>
          <DialogDescription>
            {formatYm(baseYm)} 생성 단계 데이터를 백업한 뒤 비웁니다. 초기화한
            단계부터 다시 진행해야 게시할 수 있습니다.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-foreground">
              초기화가 완료되었습니다. 지운 데이터는 아래 백업 테이블에
              남습니다.
            </p>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>단계</TableHead>
                    <TableHead className="text-right">삭제 건수</TableHead>
                    <TableHead>백업 테이블</TableHead>
                    <TableHead>비고</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.results.map((item) => (
                    <TableRow key={item.target}>
                      <TableCell>{targetLabel(item.target)}</TableCell>
                      <TableCell className="text-right">
                        {item.coveredBy
                          ? '—'
                          : item.deletedCount.toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {item.backupTableName ?? '백업 없음'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {item.coveredBy
                          ? `${targetLabel(item.coveredBy)} 초기화에 포함`
                          : item.revertedCommissionCount > 0
                            ? `확인 필요 의약품 ${item.revertedCommissionCount.toLocaleString()}건 생성 대기로 되돌림`
                            : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {released ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    게시된 기준월은 초기화할 수 없습니다. 먼저 게시를
                    취소하세요.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onCancelRelease}
                  >
                    게시 취소
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-3">
              <label className="flex items-center gap-2 border-b border-border pb-3 text-sm font-medium">
                <Checkbox
                  checked={allSelected}
                  disabled={released}
                  onCheckedChange={(v) =>
                    setSelected(v === true ? ALL_TARGETS : [])
                  }
                />
                전체
              </label>
              {DRUG_DATA_RESET_TARGETS.map((target) => (
                <label key={target.value} className="flex items-start gap-2">
                  <Checkbox
                    className="mt-0.5"
                    checked={selected.includes(target.value)}
                    disabled={released}
                    onCheckedChange={(v) => toggle(target.value, v === true)}
                  />
                  <span className="space-y-0.5">
                    <span className="block text-sm">{target.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      삭제: {target.scope} · 미완료로 변경: {target.lowers}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <p className="flex items-start gap-1.5 text-xs text-ruda-warning">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              복원 기능은 없습니다. 수기 입력 초기화는 수기로 새로 넣은 의약품만
              지우며, 기존 의약품을 수기로 수정한 내용은 되돌리지 않습니다.
            </p>

            <div className="space-y-1.5">
              <label
                htmlFor="drug-data-reset-confirm"
                className="text-sm text-foreground"
              >
                확인을 위해 기준월 <strong>{baseYm}</strong> 을 입력하세요.
              </label>
              <Input
                id="drug-data-reset-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={baseYm}
                disabled={released}
                clearable={false}
                autoComplete="off"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={onClose}>닫기</Button>
          ) : (
            <>
              <Button variant="outline" disabled={loading} onClick={onClose}>
                취소
              </Button>
              <Button
                variant="destructive"
                disabled={!canSubmit}
                onClick={() => void handleConfirm()}
              >
                {loading ? <Loader2 className="size-4 animate-spin" /> : null}
                {allSelected ? '전체 초기화' : '선택 초기화'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
